import React, { ChangeEventHandler, useEffect, useState } from 'react';
import { getCheckboxStyles, getCheckboxLabelColor } from './styleUtils';
import { Check, Minus } from 'react-feather';
import { COLOR_TYPE } from '../globalTypes';
import { Typography } from '../Typography/Typography';
import { GRAY_COLORS } from '../globalTheme';

export type checkboxIconType = 'check' | 'select';
interface CheckboxProps {
	label?: string;
	supportingText?: string;
	checked: boolean;
	onChange: ChangeEventHandler<HTMLInputElement>;
	name: string;
	disabled?: boolean;
	dataTestId?: string;
	type?: COLOR_TYPE;
	iconType?: checkboxIconType;
	rounded?: boolean;
	className?: string;
}

const getCheckboxIcon = (iconType: checkboxIconType, colorType: COLOR_TYPE) => {
	switch (iconType) {
		case 'check':
			return <Check size={12} color={getCheckboxLabelColor(colorType)} />;
		case 'select':
			return <Minus size={14} color={getCheckboxLabelColor(colorType)} />;
	}
};
const Checkbox: React.FC<CheckboxProps> = ({
	label,
	supportingText,
	checked,
	onChange,
	name,
	disabled = false,
	dataTestId,
	type = 'primary',
	iconType = 'check',
	rounded = false,
	className = ''
}) => {
	const [localChecked, setLocalChecked] = useState(checked);
	const [isFocused, setIsFocused] = useState(false);
	const styles = getCheckboxStyles();

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setLocalChecked(event.target.checked);
		onChange(event);
	};

	const keyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleChange({
				target: { name, checked: !localChecked }
			} as React.ChangeEvent<HTMLInputElement>);
		}
	};

	useEffect(() => {
		setLocalChecked(checked);
	}, [checked]);

	return (
		<div
			style={styles.container}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			onKeyDown={keyDownHandler}
			tabIndex={disabled ? -1 : 0}
			className={`print:flex print:items-center print:justify-center ${className}`}
		>
			<div style={styles.fakeCheckboxContainer} className="print:hidden">
				<div
					style={styles.fakeCheckbox(isFocused, type, localChecked, disabled, rounded)}
					className="print:hidden"
					onClick={(event) => {
						event.preventDefault();
						handleChange({
							target: { name, checked: !localChecked }
						} as React.ChangeEvent<HTMLInputElement>);
					}}
				>
					{localChecked && getCheckboxIcon(iconType, type)}
				</div>
				<input
					data-testid={dataTestId}
					type="checkbox"
					id={name}
					name={name}
					checked={localChecked}
					onChange={handleChange}
					disabled={disabled}
					style={styles.realCheckbox}
					width={16}
					height={16}
					tabIndex={-1}
					className={`${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} print:hidden`}
				/>
			</div>
			<div style={styles.textContainer} className="print:hidden">
				<label htmlFor={name}>
					<Typography
						size="sm"
						weight="medium"
						color={disabled ? GRAY_COLORS.gray300 : GRAY_COLORS.gray700}
					>
						{label || ''}
					</Typography>
				</label>
				{supportingText && (
					<Typography
						size="sm"
						weight="regular"
						color={disabled ? GRAY_COLORS.gray300 : GRAY_COLORS.gray600}
					>
						{supportingText}
					</Typography>
				)}
			</div>
			{/* Print-only checkbox */}
			<input
				type="checkbox"
				checked={localChecked}
				disabled={disabled}
				className="hidden print:block print:w-4 print:h-4 print:border-2 print:border-gray-400 print:rounded-sm print:mr-2 print:align-middle print:bg-white print:checked:bg-gray-400 print:checked:border-gray-400"
				readOnly
			/>
		</div>
	);
};

export default Checkbox;
